/**
 * Legal pages — Terms of Service AND Privacy Policy.
 *
 * Mounted at /legal/terms and /legal/privacy. Linked from the global Footer.
 * The text below is a SENSIBLE STARTING POINT — replace bracketed placeholders
 * with your actual business details before going live, and have a lawyer review
 * if you'll be processing real PII at scale.
 */
import { Link } from "react-router-dom";

const COMPANY = "GrantFlow Inc."; // EDIT ME
const SUPPORT_EMAIL = "support@grantflow.it.com"; // EDIT ME
const LEGAL_EMAIL = "support@grantflow.it.com"; // Consolidated with support
const JURISDICTION = "[Your State], USA"; // EDIT ME (e.g., "Delaware, USA")
const EFFECTIVE_DATE = "May 16, 2026"; // EDIT ME

function LegalShell({ subtitle, title, children, currentSlug }) {
  const tabs = [
    { slug: "terms", label: "Terms of Service" },
    { slug: "privacy", label: "Privacy Policy" },
    { slug: "copyright", label: "Copyright & DMCA" },
  ];
  return (
    <div className="max-w-3xl" data-testid={`legal-${currentSlug}`}>
      <div className="gf-overline">{subtitle}</div>
      <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mt-2">
        {title}
      </h1>
      <div className="mt-3 text-xs text-zinc-500 font-mono">
        Effective {EFFECTIVE_DATE} · {COMPANY}
      </div>
      <nav className="mt-6 flex gap-2 flex-wrap" data-testid="legal-tabs">
        {tabs.map((t) => (
          <Link
            key={t.slug}
            to={`/legal/${t.slug}`}
            data-testid={`legal-tab-${t.slug}`}
            className={`text-xs px-3 py-1.5 border font-mono uppercase tracking-wider ${
              t.slug === currentSlug
                ? "bg-zinc-900 text-white border-zinc-900"
                : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-900"
            }`}
          >
            {t.label}
          </Link>
        ))}
      </nav>
      <section className="mt-6 border border-zinc-200 bg-white p-6 md:p-8 prose-grant">
        {children}
      </section>
      <div className="mt-6 text-xs text-zinc-500 font-mono">
        Questions? Contact <a className="underline" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
      </div>
    </div>
  );
}

export function TermsOfService() {
  return (
    <LegalShell subtitle="Legal" title="Terms of Service." currentSlug="terms">
      <h2>1. Acceptance of terms</h2>
      <p>
        By creating an account, browsing, or using GrantFlow (the "Service"), you agree to be
        bound by these Terms of Service ("Terms"). If you do not agree, do not use the Service.
        These Terms form a binding contract between you and {COMPANY}.
      </p>

      <h2>2. Eligibility &amp; account</h2>
      <ul>
        <li>You must be at least 18 years old.</li>
        <li>One human user per account. Account sharing or shared credentials is prohibited.</li>
        <li>You are responsible for safeguarding your password.</li>
        <li>You agree to provide accurate information (name, organization, email) and update it as needed.</li>
      </ul>

      <h2>3. Subscription, billing &amp; refunds</h2>
      <p>
        Paid plans auto-renew monthly. You may cancel anytime via the Stripe Customer Portal at
        <span className="text-blue-700">/subscribe</span>. Cancellation takes effect at the end of the current paid period.
      </p>
      <p>
        Refund requests must be submitted within <b>24 hours</b> of the most recent charge via the
        Refunds section in Settings. You keep full access while your request is under review. If
        the request is <b>approved</b>, your premium access is revoked immediately and any active
        recurring subscription is canceled, so no further charges occur. If the request is
        <b> rejected</b>, your access continues as normal.
      </p>
      <ul>
        <li>
          <b>30-day Pass</b> ($19.99): refundable within 24 hours of purchase, at our discretion.
          After 24 hours, no refunds.
        </li>
        <li>
          <b>Monthly subscription</b> ($14.99/mo): pro-rata refunds within 24 hours of the most
          recent charge, at our discretion. After 24 hours, no refunds &mdash; cancel to stop renewal.
        </li>
        <li>
          <b>Lifetime</b> ($199 one-time): refundable within 24 hours of purchase, at our
          discretion. After 24 hours, no refunds.
        </li>
        <li>
          <b>Founder&apos;s Plan</b> ($5.99/mo for life, first 100 customers): price is locked for the
          subscription&apos;s lifetime as long as it remains continuously active. Lapsed subscriptions
          forfeit the Founder rate.
        </li>
        <li>
          <b>Template purchases</b>: all sales are final. Templates are digital goods.
        </li>
        <li>
          <b>Taxes</b>: prices shown exclude applicable sales tax / VAT, which is calculated and
          remitted by Stripe Tax at checkout based on your billing address.
        </li>
      </ul>

      <h2>4. Acceptable use</h2>
      <ul>
        <li>No automated scraping, crawling, or bulk extraction of Grants.gov search results via our API.</li>
        <li>No reverse engineering, decompiling, or attempting to derive source code.</li>
        <li>No reselling AI-generated proposal output as your own AI service.</li>
        <li>No use of the Service to commit fraud, including grant-application fraud.</li>
        <li>We may suspend or terminate accounts that violate these terms, with or without notice.</li>
      </ul>

      <h2>5. AI-generated content disclaimer</h2>
      <p>
        Proposal drafts are generated by Claude Sonnet 4.5 from inputs you provide.
        {" "}{COMPANY} does NOT guarantee:
      </p>
      <ul>
        <li>Accuracy or completeness of any generated text.</li>
        <li>Fundability or compliance with any specific funder's requirements.</li>
        <li>That the proposal will win, place, or even pass eligibility review.</li>
      </ul>
      <p>
        You are <b>solely responsible</b> for reviewing, editing, and verifying every draft
        before submission. Do not submit AI-generated content without human review.
      </p>

      <h2>6. Intellectual property</h2>
      <ul>
        <li>
          You retain ownership of the project data you provide and the resulting AI-generated
          proposal text for your own account.
        </li>
        <li>
          {COMPANY} retains ownership of the platform, source code, design, brand, and any
          aggregated/anonymized usage data.
        </li>
        <li>
          Marketplace templates are licensed (not sold) to buyers for internal use only.
          Redistribution of purchased templates is prohibited.
        </li>
      </ul>

      <h2>7. Liability limit</h2>
      <p>
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, {COMPANY} IS NOT LIABLE FOR ANY INDIRECT,
        INCIDENTAL, OR CONSEQUENTIAL DAMAGES, INCLUDING LOST GRANTS, LOST REVENUE, OR LOST
        OPPORTUNITY. OUR TOTAL LIABILITY IN ANY 12-MONTH PERIOD IS CAPPED AT THE AMOUNT YOU
        PAID US IN THAT PERIOD.
      </p>

      <h2>8. Termination</h2>
      <p>
        Either party may terminate this agreement at any time. Upon termination, your access to
        the Service ends and your data is retained for 30 days, then permanently deleted (except
        anonymized financial records, which we retain for 7 years per tax law).
      </p>

      <h2>9. Changes</h2>
      <p>
        We may update these Terms; material changes will be emailed to you. Continued use after
        notice constitutes acceptance.
      </p>

      <h2>10. Governing law</h2>
      <p>
        These Terms are governed by the laws of {JURISDICTION}, without regard to conflict-of-laws
        principles. Any dispute will be resolved in the state or federal courts located in
        {" "}{JURISDICTION}.
      </p>

      <h2>11. Contact</h2>
      <p>
        For Terms questions: <a className="text-blue-700 underline" href={`mailto:${LEGAL_EMAIL}`}>{LEGAL_EMAIL}</a>. For support: <a className="text-blue-700 underline" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
      </p>
    </LegalShell>
  );
}

export function PrivacyPolicy() {
  return (
    <LegalShell subtitle="Legal" title="Privacy Policy." currentSlug="privacy">
      <h2>1. What we collect</h2>
      <ul>
        <li><b>Account data</b>: email, hashed password, name, organization, role.</li>
        <li><b>Optional contact data</b>: phone number (for SMS alerts; opt-in).</li>
        <li><b>Usage data</b>: proposals you create, saved searches, comments, template purchases.</li>
        <li><b>Payment metadata</b>: Stripe customer ID, subscription state, last-4 of card. We do NOT store full credit-card numbers.</li>
        <li><b>Operational logs</b>: IP address, browser type, timestamps, error traces (retained 90 days).</li>
      </ul>

      <h2>2. How we use it</h2>
      <ul>
        <li>To operate the Service — authentication, billing, AI proposal generation, alerts.</li>
        <li>To send transactional email (receipts, grant alerts, password resets).</li>
        <li>To improve the Service (aggregated, anonymized analytics only).</li>
        <li>To detect fraud or violations of the Terms.</li>
      </ul>
      <p>
        We do <b>not</b> sell your data. We do <b>not</b> share your data with advertisers. We do
        <b> not</b> use your proposal content to train our AI models.
      </p>

      <h2>3. Third-party processors</h2>
      <ul>
        <li><b>Stripe, Inc.</b> — payment processing. <a className="underline" href="https://stripe.com/privacy">stripe.com/privacy</a></li>
        <li><b>Resend</b> — transactional email delivery. <a className="underline" href="https://resend.com/legal/privacy-policy">resend.com/legal/privacy-policy</a></li>
        <li><b>Twilio</b> — SMS delivery (only if you opt in). <a className="underline" href="https://www.twilio.com/legal/privacy">twilio.com/legal/privacy</a></li>
        <li><b>Anthropic / OpenAI / Google</b> — AI inference for proposal drafts (via Emergent LLM Key).</li>
        <li><b>MongoDB Atlas</b> — database hosting.</li>
      </ul>

      <h2>4. SMS messaging consent</h2>
      <p>
        SMS alerts are <b>opt-in only</b>. By providing your phone number in Settings, you consent
        to receive grant-alert messages from {COMPANY}. Standard message and data rates may apply.
      </p>
      <ul>
        <li>Reply <b>STOP</b> to any message to unsubscribe immediately and clear your phone number.</li>
        <li>Reply <b>HELP</b> for help.</li>
        <li>Frequency: depends on your saved-search frequency (immediate, daily, or weekly).</li>
        <li>We never share phone numbers with third parties.</li>
      </ul>

      <h2>5. Cookies</h2>
      <p>
        We use a single HTTP-only authentication cookie. No third-party tracking cookies,
        no advertising pixels, no Facebook/Google Analytics by default.
      </p>

      <h2>6. Your rights (GDPR / CCPA)</h2>
      <ul>
        <li><b>Access</b>: request a copy of all data we hold about you.</li>
        <li><b>Correction</b>: update inaccurate data via <span className="text-blue-700">/settings</span>.</li>
        <li><b>Deletion</b>: request full account + data deletion via <a className="text-blue-700 underline" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>; we process within 30 days.</li>
        <li><b>Portability</b>: request your data in JSON format.</li>
        <li><b>Withdraw consent</b>: opt out of SMS at any time via STOP.</li>
      </ul>

      <h2>7. Data retention</h2>
      <ul>
        <li>Account data: retained until you request deletion.</li>
        <li>Proposals: retained until you delete them or your account.</li>
        <li>Financial records (receipts, transactions): retained 7 years per US tax law.</li>
        <li>Operational logs: 90 days.</li>
      </ul>

      <h2>8. Security</h2>
      <p>
        Passwords are bcrypt-hashed. Database connections use TLS. Payments are routed through
        Stripe (PCI DSS Level 1). We monitor for breaches and will notify affected users within
        72 hours of any confirmed incident.
      </p>

      <h2>9. Children's privacy</h2>
      <p>
        The Service is not directed to anyone under 18. We do not knowingly collect data from
        minors. If you believe a minor has signed up, please contact <a className="text-blue-700 underline" href={`mailto:${LEGAL_EMAIL}`}>{LEGAL_EMAIL}</a>.
      </p>

      <h2>10. International users</h2>
      <p>
        Data is processed and stored in the United States. By using the Service, you consent to
        the transfer of your data to the US, which may have different data-protection laws than
        your country.
      </p>

      <h2>11. Changes</h2>
      <p>
        We will email all users at least 30 days before any material change to this Privacy
        Policy takes effect.
      </p>

      <h2>12. Contact</h2>
      <p>
        Privacy requests: <a className="text-blue-700 underline" href={`mailto:${LEGAL_EMAIL}`}>{LEGAL_EMAIL}</a>. General support: <a className="text-blue-700 underline" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
      </p>
    </LegalShell>
  );
}

export function CopyrightAndDmca() {
  const year = new Date().getFullYear();
  return (
    <LegalShell subtitle="Legal" title="Copyright & DMCA." currentSlug="copyright">
      <h2>Copyright</h2>
      <p>
        © {year} {COMPANY}. All rights reserved. The {COMPANY} name, logo, application source
        code, design system, and all platform-generated content (including AI-generated proposal
        drafts produced for an authenticated account) are the intellectual property of {COMPANY}
        {" "}or the requesting account holder, as applicable.
      </p>
      <p>
        Unauthorized duplication, scraping, redistribution, framing, or commercial reuse of any
        portion of this application — including the AI-generated proposal output, the search
        index data, or the user interface — is strictly prohibited and may result in civil and/or
        criminal liability under applicable copyright, computer-fraud, and unfair-competition
        laws.
      </p>

      <h2>DMCA takedown</h2>
      <p>
        If you believe content on the Service infringes your copyright, email{" "}
        <a className="text-blue-700 underline" href={`mailto:${LEGAL_EMAIL}`}>{LEGAL_EMAIL}</a> with:
      </p>
      <ol>
        <li>Description of the copyrighted work.</li>
        <li>URL where the infringing material appears.</li>
        <li>Your contact information.</li>
        <li>A statement of good-faith belief that use is not authorized.</li>
        <li>A statement under penalty of perjury that the info is accurate and you are authorized to act on the rights-holder's behalf.</li>
        <li>Your physical or electronic signature.</li>
      </ol>
      <p>
        We respond to valid DMCA notices within 7 business days and may remove the offending
        content. Repeat infringers have their accounts terminated.
      </p>

      <h2>Contact</h2>
      <p>DMCA agent: <a className="text-blue-700 underline" href={`mailto:${LEGAL_EMAIL}`}>{LEGAL_EMAIL}</a></p>
    </LegalShell>
  );
}

// Default export = Terms (the most common landing target).
export default TermsOfService;
